import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import SpecialistEditModal from '../../components/SpecialistEditModal/SpecialistEditModal';
import { specialistsApi } from '../../services/apiService';
import { SOCKET_URL } from '../../config/api';
import './AdminSpecialistProfilePage.css';

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${SOCKET_URL}${url}`;
  return url;
};

const statusConfig = {
  active: { label: 'Active', color: '#006928', bg: '#DCFCE7' },
  pending: { label: 'Pending', color: '#92400E', bg: '#FEF3C7' },
  inactive: { label: 'Inactive', color: '#6D6D6D', bg: '#E5E5E5' },
  suspended: { label: 'Suspended', color: '#DC2626', bg: '#FEE2E2' },
};

const AdminSpecialistProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [specialist, setSpecialist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  const fetchSpecialist = useCallback(async () => {
    try {
      setLoading(true);
      const data = await specialistsApi.getByIdAdmin(id);
      console.log('Specialist profile data:', data);
      setSpecialist(data.specialist || data);
      setError(null);
    } catch (err) {
      console.error('Error fetching specialist:', err);
      setError('Failed to load specialist profile');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchSpecialist();
    }
  }, [id, fetchSpecialist]);

  const handleStatusChange = async (newStatus) => {
    if (!specialist || statusLoading) return;
    try {
      setStatusLoading(true);
      await specialistsApi.update(specialist.id, { status: newStatus });
      setSpecialist(prev => ({ ...prev, status: newStatus }));
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleEditUpdate = (updatedSpecialist) => {
    if (updatedSpecialist) {
      setSpecialist(updatedSpecialist);
    } else {
      fetchSpecialist();
    }
    setShowEditModal(false);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  // Loading state — render AdminLayout with loading indicator
  if (loading) {
    return (
      <AdminLayout>
        <div className="asp-content">
          <div className="asp-loading">
            <div className="asp-spinner" />
            <p>Loading specialist profile...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Error or not found state
  if (error || !specialist) {
    return (
      <AdminLayout>
        <div className="asp-content">
          <div className="asp-error">
            <p>{error || 'Specialist not found.'}</p>
            <button onClick={() => navigate('/admin/specialists')}>Back to Specialists</button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const sConf = statusConfig[specialist.status] || statusConfig.inactive;

  return (
    <AdminLayout>
      <div className="asp-content">
        {/* Back + Actions Bar */}
        <div className="asp-top-bar">
          <button className="asp-back-btn" onClick={() => navigate('/admin/specialists')}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Back to Specialists
          </button>
        </div>

        {/* Profile Header Card */}
        <div className="asp-header-card">
          <div className="asp-header-left">
            {specialist.avatar ? (
              <img className="asp-avatar" src={getImageUrl(specialist.avatar)} alt={specialist.name} />
            ) : (
              <div className="asp-avatar-placeholder">{getInitials(specialist.name)}</div>
            )}
            <div className="asp-header-info">
              <h1 className="asp-name">{specialist.name}</h1>
              {specialist.title && <p className="asp-title">{specialist.title}</p>}
              <div className="asp-header-meta">
                <span className="asp-status-badge" style={{ color: sConf.color, background: sConf.bg }}>
                  {sConf.label}
                </span>
                {specialist.membershipPlan && (
                  <span className="asp-plan-badge">{specialist.membershipPlan}</span>
                )}
                {specialist.emailVerified && (
                  <span className="asp-verified-badge">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M9 12l2 2 4-4" stroke="#006928" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="12" r="10" stroke="#006928" strokeWidth="2"/></svg>
                    Verified
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="asp-header-actions">
            <select
              className="asp-status-select"
              value={specialist.status || 'active'}
              onChange={(e) => handleStatusChange(e.target.value)}
              disabled={statusLoading}
            >
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
            <button className="asp-edit-btn" onClick={() => setShowEditModal(true)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              Edit Profile
            </button>
          </div>
        </div>

        {/* Details Grid */}
        <div className="asp-details-grid">
          <div className="asp-card">
            <h3 className="asp-card-title">Contact Information</h3>
            <div className="asp-field-list">
              <div className="asp-field">
                <span className="asp-label">Email</span>
                <span className="asp-value">{specialist.email || '—'}</span>
              </div>
              <div className="asp-field">
                <span className="asp-label">Phone</span>
                <span className="asp-value">{specialist.phone || '—'}</span>
              </div>
              <div className="asp-field">
                <span className="asp-label">Country</span>
                <span className="asp-value">{specialist.country || '—'}</span>
              </div>
              <div className="asp-field">
                <span className="asp-label">City</span>
                <span className="asp-value">{specialist.city || '—'}</span>
              </div>
            </div>
          </div>

          <div className="asp-card">
            <h3 className="asp-card-title">Professional Details</h3>
            <div className="asp-field-list">
              <div className="asp-field">
                <span className="asp-label">Category</span>
                <span className="asp-value">{specialist.category || '—'}</span>
              </div>
              <div className="asp-field">
                <span className="asp-label">Experience</span>
                <span className="asp-value">{specialist.experience || '—'}</span>
              </div>
              <div className="asp-field">
                <span className="asp-label">Company</span>
                <span className="asp-value">{specialist.companyName || '—'}</span>
              </div>
              <div className="asp-field">
                <span className="asp-label">Expertise</span>
                <span className="asp-value">{specialist.expertise || '—'}</span>
              </div>
            </div>
          </div>

          <div className="asp-card">
            <h3 className="asp-card-title">Account Details</h3>
            <div className="asp-field-list">
              <div className="asp-field">
                <span className="asp-label">Membership Plan</span>
                <span className="asp-value">{specialist.membershipPlan || '—'}</span>
              </div>
              <div className="asp-field">
                <span className="asp-label">Email Verified</span>
                <span className="asp-value">{specialist.emailVerified ? 'Yes' : 'No'}</span>
              </div>
              <div className="asp-field">
                <span className="asp-label">Interested Locations</span>
                <span className="asp-value">{specialist.interestedLocations || '—'}</span>
              </div>
              <div className="asp-field">
                <span className="asp-label">Joined</span>
                <span className="asp-value">
                  {specialist.createdAt ? new Date(specialist.createdAt).toLocaleDateString() : '—'}
                </span>
              </div>
              <div className="asp-field">
                <span className="asp-label">Last Login</span>
                <span className="asp-value">
                  {specialist.lastLogin ? new Date(specialist.lastLogin).toLocaleDateString() : '—'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bio Section */}
        {specialist.bio && (
          <div className="asp-card asp-bio-card">
            <h3 className="asp-card-title">Bio</h3>
            <p className="asp-bio-text">{specialist.bio}</p>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <SpecialistEditModal
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            specialist={specialist}
            onUpdate={handleEditUpdate}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSpecialistProfilePage;
